import { devlog, devwarn } from '../../logic/utils.ts';
import styleText from './as-popout-card.css?raw';

/**
 * # ASPopoutCard Component
 * 
 * A card component that can be shown/hidden and positioned relative to a button.
 * 
 * ## Features
 * - Can be shown/hidden via the `show` attribute
 * - Configurable placement relative to a button via the `placement` attribute
 * - Uses CSS variables for positioning and dimensions
 * - Emits events when visibility changes
 * - Handles its own positioning based on placement
 * - Closes when clicking outside the card
 * 
 * ## Component Hierarchy
 * ```
 * ASPopoutCard (as-popout-card)
 * └── slot - For the card content
 * ```
 * 
 * ## Attributes
 * - `show`: When present, shows the card
 * - `placement`: Controls where the card appears relative to the button (default: "right")
 *   - Supported values: "right", "left", "top", "bottom", "bottom-left", "bottom-right", "top-left", "top-right"
 * 
 * ## CSS Variables
 * - `--offset-x`: Horizontal offset from the reference point (default: 0)
 * - `--offset-y`: Vertical offset from the reference point (default: 0)
 * - `--offset-x-pad`: Additional horizontal padding (default: 0px)
 * - `--gap`: Gap between the reference point and the card (default: 10px)
 * - `--min-width`: Minimum width of the card (default: 10rem)
 * - `--max-width`: Maximum width of the card (default: 32rem)
 * - `--max-height`: Maximum height of the card
 * - `--padding-global`: Padding inside the card (default: 2rem)
 * - `--shadow`: Box shadow for the card (default: 0 2px 10px rgba(0, 0, 0, 0.1))
 * - `--border-radius`: Border radius of the card (default: 1rem)
 * - `--border`: Border of the card (default: none)
 * - `--background`: Background color of the card (default: rgb(250, 250, 250,0.7))
 * - `--position-style`: Position style of the card (default: relative)
 * 
 * ## Events
 * ### Emitted Events
 * - `visibility-changed`: Dispatched when the card's visibility changes
 *   - Detail: `{ visible: boolean }`
 */
export class ASPopoutCard extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  private isVisible: boolean = false;
  private placement: string = "right";
  private gap: number = 10; // Gap between button and card in pixels
  private outsideClickHandler: (e: MouseEvent) => void;
  private buttonElement: HTMLElement | null = null;
  private toggleTimeout: number | null = null;
  private clickLock: boolean = false;
  private clickLockTimeout: number | null = null;

  /**
   * Define the observed attributes for the component
   */
  static get observedAttributes(): string[] {
    return ['show', 'placement'];
  }

  constructor() {
    super();
    
    // Create shadow DOM with template content
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      ASPopoutCard.sheet
    ];
    this.render();
    
    // Bind the outside click handler
    this.outsideClickHandler = this.handleOutsideClick.bind(this);
  }

  /**
   * Renders the component template with current state
   */
  private render(): void {
    this.shadowRoot!.innerHTML = /*html*/`
      <slot></slot>
    `;
  }

  /**
   * Handles changes to observed attributes
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === 'show' && oldValue !== newValue) {
      this.isVisible = this.hasAttribute('show');
      this.updateVisibility();
    } else if (name === 'placement' && oldValue !== newValue) {
      // Validate placement value
      const validPlacements = ['right', 'left', 'top', 'bottom', 'bottom-left', 'bottom-right', 'top-left', 'top-right'];
      this.placement = validPlacements.includes(newValue) ? newValue : 'right';
      
      // Reposition the card if it's visible
      if (this.isVisible) {
        this.positionCard();
      }
    }
  }

  /**
   * Sets up event listeners when the component is connected to the DOM
   */
  connectedCallback() {
    // Set initial visibility state
    this.isVisible = this.hasAttribute('show');
    this.updateVisibility();
    
    // Set initial placement
    if (this.hasAttribute('placement')) {
      const placementAttr = this.getAttribute('placement');
      if (placementAttr) {
        const validPlacements = ['right', 'left', 'top', 'bottom', 'bottom-left', 'bottom-right', 'top-left', 'top-right'];
        this.placement = validPlacements.includes(placementAttr) ? placementAttr : 'right';
      }
    }
    
    // Listen for the toggle-visibility event
    this.addEventListener('toggle-visibility', this.handleToggleVisibility);
    
    // Find the parent button element
    this.findParentButton();
  }

  /**
   * Finds the parent button element that controls this card
   */
  private findParentButton(): void {
    // Look for a parent element that might be the button
    let parent = this.parentElement;
    while (parent) {
      if (parent.tagName === 'AS-POPOUT-BUTTON' || 
          parent.tagName === 'AS-ICON-BUTTON' || 
          parent.tagName === 'AS-TEXT-BUTTON') {
        this.buttonElement = parent as HTMLElement;
        break;
      }
      parent = parent.parentElement;
    }
  }

  /**
   * Handles the toggle-visibility event
   */
  private handleToggleVisibility = (event: Event) => {
    // Prevent event from bubbling further
    event.stopPropagation();
    
    // If we're in a click lock, ignore this toggle
    if (this.clickLock) {
      return;
    }
    
    // Debounce the toggle to prevent multiple rapid toggles
    if (this.toggleTimeout !== null) {
      window.clearTimeout(this.toggleTimeout);
    }
    
    this.toggleTimeout = window.setTimeout(() => {
      // Toggle the show attribute
      if (this.hasAttribute('show')) {
        this.removeAttribute('show');
        this.isVisible = false;
      } else {
        this.setAttribute('show', '');
        this.isVisible = true;
      }
      
      // Update visibility and dispatch event
      this.updateVisibility();
      
      // Reset the timeout
      this.toggleTimeout = null;
    }, 50); // Small delay to debounce
  }

  /**
   * Updates the visibility state and dispatches an event
   */
  private updateVisibility(): void {
    // Position the card before showing it
    if (this.isVisible) {
      this.positionCard();
      
      // Add outside click listener when showing
      setTimeout(() => {
        document.addEventListener('mousedown', this.outsideClickHandler);
      }, 100); // Small delay to prevent immediate firing when clicking the button
    } else {
      // Remove outside click listener when hiding
      document.removeEventListener('mousedown', this.outsideClickHandler);
    }
    
    // Dispatch the visibility-changed event
    this.dispatchEvent(new CustomEvent('visibility-changed', {
      bubbles: true,
      composed: true,
      detail: { visible: this.isVisible }
    }));
  }

  /**
   * Handles clicks outside the card to close it
   */
  private handleOutsideClick = (e: MouseEvent): void => {
    devlog("handleOutsideClick", e);
    
    // If the event was already handled by the popout card click handler, ignore it
    if ((e as any)._handledByPopoutCard) {
      devlog("Event was already handled by popout card, ignoring");
      return;
    }
    
    // Check if the click is outside the card and not on the button
    const isOutsideCard = !this.contains(e.target as Node);
    const isOnButton = this.buttonElement?.contains(e.target as Node);
    
    if (isOutsideCard && !isOnButton) {
      // Set click lock to prevent toggle from firing immediately after
      this.clickLock = true;
      
      // Clear any existing click lock timeout
      if (this.clickLockTimeout !== null) {
        window.clearTimeout(this.clickLockTimeout);
      }
      
      // Close the card
      this.isVisible = false;
      this.removeAttribute('show');
      this.updateVisibility();
      
      // Release the click lock after a delay
      this.clickLockTimeout = window.setTimeout(() => {
        this.clickLock = false;
        this.clickLockTimeout = null;
      }, 300); // Longer delay to ensure toggle doesn't fire
    }
  }

  /**
   * Positions the card based on the placement attribute
   */
  private positionCard(): void {
    // Get the card dimensions
    const cardWidth = this.offsetWidth || 200; // Default width if not yet rendered
    const cardHeight = this.offsetHeight || 100; // Default height if not yet rendered
    
    // Get the gap from CSS variable or use default
    const gap = parseInt(getComputedStyle(this).getPropertyValue('--gap') || '10');
    
    // Position the card based on placement
    switch (this.placement) {
      case 'right':
        this.style.setProperty('--offset-x', `calc(var(--reference-width, 0px) + ${gap}px)`);
        this.style.setProperty('--offset-y', '0px');
        break;
      case 'left':
        this.style.setProperty('--offset-x', `calc(-${cardWidth}px - ${gap}px)`);
        this.style.setProperty('--offset-y', '0px');
        break;
      case 'top':
        this.style.setProperty('--offset-x', '0px');
        this.style.setProperty('--offset-y', `calc(-${cardHeight}px - ${gap}px)`);
        break;
      case 'bottom':
        this.style.setProperty('--offset-x', '0px');
        this.style.setProperty('--offset-y', `calc(var(--reference-height, 0px) + ${gap}px)`);
        break;
      case 'bottom-left':
        this.style.setProperty('--offset-x', `calc(var(--reference-width, 0px) - ${cardWidth}px)`);
        this.style.setProperty('--offset-y', `calc(var(--reference-height, 0px) + ${gap}px)`);
        break;
      case 'bottom-right':
        this.style.setProperty('--offset-x', '0px');
        this.style.setProperty('--offset-y', `calc(var(--reference-height, 0px) + ${gap}px)`);
        break;
      case 'top-left':
        this.style.setProperty('--offset-x', `calc(var(--reference-width, 0px) - ${cardWidth}px)`);
        this.style.setProperty('--offset-y', `calc(-${cardHeight}px - ${gap}px)`);
        break;
      case 'top-right':
        this.style.setProperty('--offset-x', '0px');
        this.style.setProperty('--offset-y', `calc(-${cardHeight}px - ${gap}px)`);
        break;
      default:
        // Default to right if placement is invalid
        this.style.setProperty('--offset-x', `calc(var(--reference-width, 0px) + ${gap}px)`);
        this.style.setProperty('--offset-y', '0px');
    }
  }

  /**
   * Clean up event listeners when the element is removed from the DOM
   */
  disconnectedCallback() {
    this.removeEventListener('toggle-visibility', this.handleToggleVisibility);
    document.removeEventListener('mousedown', this.outsideClickHandler);
  }
}

customElements.define('as-popout-card', ASPopoutCard);
